import React, { useEffect, useState } from "react";
import {
  Box, Typography, Button, Dialog, DialogTitle, DialogContent,
  TextField, IconButton, CircularProgress, Card, CardContent,
  Grid, Stack, MenuItem, Divider, Fab, Chip, Alert
} from "@mui/material";

import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Refresh as RefreshIcon,
  CalendarMonth as CalendarIcon,
  EventNote as EventIcon
} from "@mui/icons-material";

import Swal from "sweetalert2";
import { db } from "../../../firebase"; // Adjust path to your firebase config
import {
  collection, addDoc, updateDoc, deleteDoc, doc, getDocs, query, orderBy
} from "firebase/firestore";

const PRIMARY_INDIGO = "#6366F1";

function Academiccalender() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);

  const [form, setForm] = useState({
    title: "",
    date: new Date().toISOString().split("T")[0],
    description: "",
    type: "Event"
  });

  useEffect(() => {
    fetchCalendar();
  }, []);

  const fetchCalendar = async () => {
    setLoading(true);
    try {
      const q = query(collection(db, "academic_calendar"), orderBy("date", "asc"));
      const snap = await getDocs(q);
      setEvents(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async () => {
    if (!form.title || !form.date || !form.description) {
      Swal.fire("Error", "All fields are required", "error");
      return;
    }

    setLoading(true);
    try {
      if (editId) {
        await updateDoc(doc(db, "academic_calendar", editId), form);
        Swal.fire("Updated", "Calendar updated successfully", "success");
      } else {
        await addDoc(collection(db, "academic_calendar"), form);
        Swal.fire("Created", "New event added", "success");
      }
      resetForm();
      fetchCalendar();
    } catch (err) {
      Swal.fire("Error", err.message, "error");
    }
    setLoading(false);
  };

  const handleDelete = async (id) => {
    const res = await Swal.fire({
      title: "Are you sure?",
      text: "This event will be permanently removed.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: PRIMARY_INDIGO,
      cancelButtonColor: "#d33"
    });

    if (res.isConfirmed) {
      await deleteDoc(doc(db, "academic_calendar", id));
      fetchCalendar();
      Swal.fire("Deleted!", "Event has been removed.", "success");
    }
  };

  const handleEdit = (event) => {
    setForm({
      title: event.title,
      date: event.date,
      description: event.description,
      type: event.type
    });
    setEditId(event.id);
    setOpen(true);
  };

  const resetForm = () => {
    setForm({
      title: "",
      date: new Date().toISOString().split("T")[0],
      description: "",
      type: "Event"
    });
    setEditId(null);
    setOpen(false);
  };

  return (
    <Box p={1} sx={{ backgroundColor: "#F8FAFC", minHeight: "100vh" }}>
      {/* Header Section */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={4}>
        <Box>
          <Typography variant="h4" fontWeight="900" color="#1E293B">
            Academic Calendar
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage holidays, exams, and school events
          </Typography>
        </Box>
        <Stack direction="row" spacing={2}>
          <IconButton onClick={fetchCalendar} sx={{ color: PRIMARY_INDIGO }}>
            <RefreshIcon />
          </IconButton>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setOpen(true)}
            sx={{
              bgcolor: PRIMARY_INDIGO,
              borderRadius: 2,
              px: 3,
              "&:hover": { bgcolor: "#4F46E5" }
            }}
          >
            Add Event
          </Button>
        </Stack>
      </Stack>

      {/* Main List */}
      {loading && events.length === 0 ? (
        <Box textAlign="center" py={10}>
          <CircularProgress sx={{ color: PRIMARY_INDIGO }} />
        </Box>
      ) : (
        <Grid className="row" spacing={3}>
          {events.map((event) => (
            <Grid className="col-sm-6 mb-3" item xs={12} md={6} lg={4} key={event.id}>
              <Card sx={{ borderRadius: 4, borderLeft: `6px solid ${event.type === 'Holiday' ? '#EF4444' : PRIMARY_INDIGO}`, boxShadow: "0 4px 12px rgba(0,0,0,0.05)" }}>
                <CardContent>
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                    <Box>
                      <Chip 
                        label={event.type.toUpperCase()} 
                        size="small" 
                        sx={{ 
                          mb: 1, 
                          fontWeight: "bold", 
                          fontSize: "10px",
                          bgcolor: event.type === 'Holiday' ? '#FEE2E2' : '#EEF2FF',
                          color: event.type === 'Holiday' ? '#B91C1C' : PRIMARY_INDIGO
                        }} 
                      />
                      <Typography variant="h6" fontWeight="bold" color="#1E293B">
                        {event.title}
                      </Typography>
                    </Box>
                    <Stack direction="row">
                      <IconButton size="small" onClick={() => handleEdit(event)} color="primary">
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleDelete(event.id)} color="error">
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  </Stack>

                  <Stack direction="row" alignItems="center" spacing={1} mt={1} mb={2}>
                    <CalendarIcon sx={{ fontSize: 16, color: "#64748B" }} />
                    <Typography variant="body2" fontWeight="bold" color="#64748B">
                      {new Date(event.date).toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' })}
                    </Typography>
                  </Stack>

                  <Typography variant="body2" color="text.secondary" sx={{ minHeight: 40 }}>
                    {event.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Modal for Add/Edit */}
      <Dialog open={open} onClose={resetForm} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: 4 } }}>
        <DialogTitle sx={{ fontWeight: "bold", display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {editId ? "Update Event" : "Create New Event"}
          <IconButton onClick={resetForm}><AddIcon style={{ transform: 'rotate(45deg)' }} /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={3} mt={1}>
            <TextField
              fullWidth
              label="Event Title"
              name="title"
              value={form.title}
              onChange={handleChange}
              placeholder="e.g. Annual Sport Day"
            />
            
            <Stack direction="row" spacing={2}>
              <TextField
                fullWidth
                type="date"
                label="Event Date"
                name="date"
                value={form.date}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                select
                fullWidth
                label="Type"
                name="type"
                value={form.type}
                onChange={handleChange}
              >
                <MenuItem value="Event">School Event</MenuItem>
                <MenuItem value="Holiday">Public Holiday</MenuItem>
                <MenuItem value="Exam">Examination</MenuItem>
              </TextField>
            </Stack>

            <TextField
              fullWidth
              multiline
              rows={3}
              label="Description"
              name="description"
              value={form.description}
              onChange={handleChange}
              placeholder="Provide more details about this schedule..."
            />
          </Stack>
        </DialogContent>
        <Box p={3} display="flex" justifyContent="flex-end" gap={2}>
          <Button onClick={resetForm} color="inherit" sx={{ fontWeight: "bold" }}>Cancel</Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained" 
            sx={{ bgcolor: PRIMARY_INDIGO, fontWeight: "bold", px: 4 }}
            disabled={loading}
          >
            {editId ? "Update Calendar" : "Save Event"}
          </Button>
        </Box>
      </Dialog>
    </Box>
  );
}

export default Academiccalender;